import { useEffect, useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridColumnMenuFilter,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { appStore } from '../stores/app-store';
import { ColumnMenu } from '../components/columnMenu';
import { useNavigate } from 'react-router-dom';
import { ActionsMylibrary } from './command-grid/command-grid-mylibrary';
import { CustomFilterLanguage } from './filter/custom-filter-language';
import { Button } from '@progress/kendo-react-buttons';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { MyLibraryCardView } from './mylibrary-cardview';
import { CardViewDataStateChangeEvent } from './organisations-cardview';
import { CopyGameAsync, DeleteGameAsync } from '../services/games';
import YesNoDialog from '../components/dialogs/yes-no-dialog';
import { YesNoDialogResult } from '../types/dialog-result';
import { formatDateToDDMMYYYY } from '../utils/date';
import HelpSupport from '../components/help-support';
import { useToast } from '../contexts/toast-context';
import { useGetGames } from '../hooks/services/use-games';
import { Loader } from '@progress/kendo-react-indicators';
import { useGameExport } from '../hooks/use-game-export';
import { useAuth } from 'react-oidc-context';
import { useGameImport } from '../hooks/use-game-import';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const MylibraryGrid = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const userRoles = (auth.user?.profile.role as string[]) || [];
  const hasImportPermission = userRoles.includes('ImportGames');
  const hasExportPermission = userRoles.includes('ExportGames');

  const { triggerImportFileSelect, renderImportDialogs } = useGameImport();

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  const [selectedId, setSelectedId] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [importedGameId, setImportedGameId] = useState<number>();

  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetGames(
    toDataSourceRequestString(dataState)
  );

  const { triggerExport, renderExportProgressDialog } = useGameExport();

  const handleTitleClick = (id: number) => {
    navigate(`../designer/${+id.toString()}`, {
      state: { from: '/mylibrary/games' }
    });
  };

  const onColumnTitleClick = (props: GridCellProps) => (
    <EditCommandCell {...props} />
  );

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td
        title={props.dataItem.shortDescription || props.dataItem.name}
        className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer p-0 text-start'}
          onClick={() => handleTitleClick(props.dataItem.id)}>
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  const onExport = async (id: number, name: string) => {
    if (!id) {
      onToastOpen({
        style: 'error',
        title: 'Export Game',
        body: <div>Game ID is not available.</div>
      });
      return;
    }
    const gameTitle = name || `game_${id}`;
    triggerExport(id, gameTitle);
  };

  const handleCopyLibrary = async (id: number) => {
    try {
      appStore.showLoading();

      await CopyGameAsync(id, false);

      onToastOpen({
        title: 'My library',
        body: <div>Game copied to My Library.</div>,
        style: 'success'
      });
    } catch (err: any) {
      onToastOpen({
        title: 'My library',
        body: <div>Failed to copy to My Library.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
      mutate();
    }
  };

  const CommandCell = (props: GridCellProps) => (
    <ActionsMylibrary
      onCopy={handleCopyLibrary}
      onView={onViewLibrary}
      onEdit={onEditLibrary}
      onDelete={onDelete}
      onExport={onExport}
      {...(hasExportPermission && {
        canExport: true
      })}
      {...props}></ActionsMylibrary>
  );

  const StatusName = (props: GridCellProps) => (
    <td className={'k-table-td'}>
      {(() => {
        switch (props.dataItem.status) {
          case 'New':
            return 'Draft';
          case 'Published':
          case 'Rejected':
            return 'Published';
          case 'Pending':
            return 'Published/Submitted';
          case 'Approved':
            return 'Published/Global Library';
          default:
            return '{props.dataItem.status}';
        }
      })()}
    </td>
  );

  const onAddLibrary = () => {
    navigate('../mylibrary/games/new');
  };

  const onViewLibrary = (id: number) => {
    navigate('../global-games/' + id.toString() + '?page=mylibrary');
  };

  const onEditLibrary = (id: number) => {
    navigate(`../designer/${+id.toString()}`);
  };

  const onDelete = (id: number) => {
    onDeleteLibrary(id);
  };

  const onDeleteLibrary = async (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      try {
        appStore.showLoading();

        await DeleteGameAsync(selectedId);
        mutate();

        onToastOpen({
          title: 'My Library',
          body: <div>Game deleted.</div>,
          style: 'success'
        });
      } catch {
        onToastOpen({
          title: 'My Library',
          body: <div>Failed to delete game.</div>,
          style: 'error'
        });
      } finally {
        appStore.hideLoading();
      }
    }
    setShowDeleteConfirm(false);
  };

  const onComfirmSuccessImport = (result: YesNoDialogResult) => {
    if (result === 'yes') {
      handleTitleClick(importedGameId ?? 0);
    } else {
      setImportedGameId(undefined);
    }
  };

  const closeDeleteDialog = () => {
    setShowDeleteConfirm(false);
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps, type: 'created' | 'modified') => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(
          props.dataItem[
            type === 'created' ? 'createdDateUtc' : 'modifiedDateUtc'
          ]
        )}
      </td>
    );
  };

  const CellEllipsisAndTooltip = (props: GridCellProps, field: string) => {
    return (
      <td title={props.dataItem[field]} className={'k-table-td'}>
        <div className={'grid-ellipsis-cell'}>{props.dataItem[field]}</div>
      </td>
    );
  };

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar title={'Games'}>
        <HelpSupport
          title="My games library guide"
          url="https://forum.catalystglobal.com/t/4271"
        />
        <Button onClick={onAddLibrary} themeColor={'primary'}>
          Add new
        </Button>
        {hasImportPermission && (
          <Button onClick={triggerImportFileSelect} themeColor={'primary'}>
            Import Game
          </Button>
        )}
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search assets'}
          columnsToSearch={['name', 'mapType', 'languages', 'status', 'source']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`mylibraries.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-mylibrary'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field={'name'}
                title={'Name'}
                columnMenu={ColumnMenu}
                cells={{ data: (props) => onColumnTitleClick(props) }}
              />
              <Column
                field={'mapType'}
                title={'Map Type'}
                width={130}
                filter={'text'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'createdDateUtc'}
                title={'Created'}
                width={110}
                cells={{ data: (props) => DateCell(props, 'created') }}
              />
              <Column
                field={'modifiedDateUtc'}
                title={'Updated'}
                width={110}
                cells={{ data: (props) => DateCell(props, 'modified') }}
              />
              <Column
                field={'source'}
                title={'Source'}
                width={110}
                filter={'text'}
                columnMenu={ColumnMenu}
              />
              <Column
                title={'Status'}
                field={'status'}
                width={180}
                columnMenu={ColumnMenu}
                cells={{ data: (props) => StatusName(props) }}
              />
              <Column
                field={'languages'}
                title={'Languages'}
                width={200}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'languages')
                }}
                columnMenu={(props: any) => (
                  <GridColumnMenuFilter
                    {...props}
                    filterUI={CustomFilterLanguage}
                  />
                )}
              />
              <Column
                title={'Actions'}
                width={210}
                cells={{ data: (props) => CommandCell(props) }}
              />
              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}
          {gridMode === 'card' && (
            <MyLibraryCardView
              data={data?.data ?? []}
              onTitlelick={handleTitleClick}
              onCopy={handleCopyLibrary}
              onView={handleTitleClick}
              onEdit={onEditLibrary}
              onDelete={onDeleteLibrary}
              total={data?.total}
              {...dataState}
              onDataStateChange={(e: CardViewDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
            />
          )}
        </ExcelExport>

        {showDeleteConfirm && (
          <YesNoDialog
            title={'Remove Library'}
            onConfirm={onConfirmDelete}
            onClose={closeDeleteDialog}>
            Are you sure you want to delete this game?
          </YesNoDialog>
        )}

        {importedGameId && (
          <YesNoDialog
            title={'Import Game'}
            cancelText={'Stay in page'}
            confirmText={'Go to imported game'}
            onConfirm={onComfirmSuccessImport}
            onClose={() => setImportedGameId(undefined)}>
            Game imported successfully.
          </YesNoDialog>
        )}

        {renderImportDialogs((newGameId) => {
          setImportedGameId(newGameId);
          mutate();
        })}
      </div>

      {renderExportProgressDialog()}
    </>
  );
};
export default MylibraryGrid;
